import { useState, useEffect } from 'react';

/**
 * Custom hook yang berfungsi seperti useState, tetapi menyimpan state
 * secara otomatis ke dalam localStorage.
 * @param key - Kunci unik untuk data di localStorage.
 * @param initialValue - Nilai awal jika tidak ada data di localStorage.
 * @returns Tuple [storedValue, setValue] yang mirip dengan useState.
 */
export function useLocalStorage<T>(key: string, initialValue: T) {
    // 1. Dapatkan nilai dari localStorage atau gunakan nilai awal
    const [storedValue, setStoredValue] = useState<T>(() => {
        try {
            // Cek apakah kode berjalan di browser
            if (typeof window === 'undefined') {
                return initialValue;
            }

            const item = window.localStorage.getItem(key);
            // Jika ada item, parse. Jika tidak, gunakan initialValue.
            return item ? JSON.parse(item) : initialValue;
        } catch (error) {
            // Jika terjadi error saat parsing, kembalikan nilai awal
            console.error(`Error reading localStorage key “${key}”:`, error);
            return initialValue;
        }
    });

    // 2. Gunakan useEffect untuk menyimpan nilai ke localStorage setiap kali state berubah
    useEffect(() => {
        try {
            if (typeof window !== 'undefined') {
                // Simpan state ke localStorage dalam bentuk string JSON
                window.localStorage.setItem(key, JSON.stringify(storedValue));
            }
        } catch (error) {
            // Menangani error jika localStorage penuh, dll.
            console.error(`Error setting localStorage key “${key}”:`, error);
        }
    }, [key, storedValue]); // <-- Efek ini berjalan jika key atau storedValue berubah

    return [storedValue, setStoredValue] as const;
}